/**
 * Capsule drafts — the creator's list of capsules saved but not published yet.
 *
 * The records themselves live on the creator's PDS (`status: 'draft'`), which is
 * the whole point: half-finished work has to be there when the creator opens the
 * studio on their laptop, not only on the phone that saved it. The studio reads
 * that list from the server (`app.bsky.feed.getActorCapsules`), because unfinished
 * work belongs to the creator rather than to a device.
 *
 * This module holds the device-local index that sits alongside it: an instant list
 * before the network answers, and a record of what this device last saw. Nothing
 * here is authoritative — it is a cache, and the merge below says so.
 *
 * The book studio's equivalent is `#/lib/flashbookDrafts`, and the two are
 * deliberately near-identical in shape. They are not the *same* module because the
 * things a row shows differ: a book is recognised by its cover art, and a capsule
 * has no artwork to show — its face is its theme and how many slides it has.
 */
import {device} from '#/storage'

export type CapsuleDraftRef = {
  /** AT-URI of the app.bsky.feed.capsule record. */
  uri: string
  title: string
  /** Content slides, excluding the cover. */
  slideCount: number
  /** A theme the capsule names, for the row to say which one it wears. */
  themeId?: string
  /** ISO timestamp of the last save, newest first in the list. */
  updatedAt: string
}

/** Enough history to recover recent work without becoming a junk drawer. */
const MAX_DRAFTS = 20

/** Newest first, and never longer than the history we offer. */
function order(drafts: CapsuleDraftRef[]): CapsuleDraftRef[] {
  return [...drafts]
    .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
    .slice(0, MAX_DRAFTS)
}

/**
 * This device's index, in the shape a row renders from.
 *
 * Normalised on the way *out* rather than merely trusted, because this key is
 * persistent storage: it outlives app versions, and its rows are painted straight
 * from what it holds, so a value written by any writer — now or later — must not be
 * able to reach a row as a shape the row cannot read.
 */
export function getCapsuleDrafts(): CapsuleDraftRef[] {
  const stored = device.get(['capsuleDrafts']) ?? []
  return stored.map(draft => ({
    uri: String(draft.uri),
    title:
      typeof draft.title === 'string' && draft.title.trim()
        ? draft.title
        : 'Untitled capsule',
    slideCount: typeof draft.slideCount === 'number' ? draft.slideCount : 0,
    ...(typeof draft.themeId === 'string' && draft.themeId
      ? {themeId: draft.themeId}
      : {}),
    updatedAt: typeof draft.updatedAt === 'string' ? draft.updatedAt : '',
  }))
}

/** Adds (or refreshes) a draft and returns the new index. */
export function rememberCapsuleDraft(
  draft: CapsuleDraftRef,
): CapsuleDraftRef[] {
  const next = order([
    draft,
    ...getCapsuleDrafts().filter(d => d.uri !== draft.uri),
  ])
  device.set(['capsuleDrafts'], next)
  return next
}

/** Drops a draft from the index (published, discarded, or gone). */
export function forgetCapsuleDraft(uri: string): CapsuleDraftRef[] {
  const next = getCapsuleDrafts().filter(d => d.uri !== uri)
  device.set(['capsuleDrafts'], next)
  return next
}

/**
 * The part of an `app.bsky.feed.getActorCapsules` item this list needs.
 *
 * Declared structurally rather than imported, so the mapping stays testable in a
 * plain node environment and the studio is not coupled to generated types.
 */
export type CapsuleDraftView = {
  uri?: string
  title?: string
  slideCount?: number
  slides?: unknown[]
  themeId?: string
  /** The record's save time as the server indexes it. */
  indexedAt?: string
}

/**
 * Maps a server view to a draft reference.
 *
 * `indexedAt` rather than the record's own `createdAt`, which is the honest thing
 * to show: re-saving a draft deliberately keeps the original `createdAt`, so it
 * would claim a two-week-old draft was "just now". `indexedAt` is when this save
 * became visible.
 */
export function capsuleDraftRefFromView(
  view: CapsuleDraftView,
): CapsuleDraftRef | null {
  if (!view.uri) return null
  const slideCount =
    typeof view.slideCount === 'number'
      ? view.slideCount
      : Array.isArray(view.slides)
        ? view.slides.length
        : 0
  return {
    uri: view.uri,
    title: view.title?.trim() || 'Untitled capsule',
    slideCount,
    ...(view.themeId ? {themeId: view.themeId} : {}),
    updatedAt: view.indexedAt ?? '',
  }
}

/**
 * Merges the server's list with what this device remembers.
 *
 * The server wins every conflict — it is the only thing that knows a draft was
 * published or deleted somewhere else. A local entry the server does not list is
 * *kept* rather than pruned, deliberately: a save made a moment ago may not have
 * been indexed yet, and dropping it would hide the creator's work at the exact
 * moment they saved it. The cost of that choice is a stale row until something
 * touches it, which is the far cheaper mistake.
 */
export function mergeCapsuleDrafts(
  local: CapsuleDraftRef[],
  remote: CapsuleDraftRef[],
): CapsuleDraftRef[] {
  const byUri = new Map<string, CapsuleDraftRef>()
  for (const draft of local) byUri.set(draft.uri, draft)
  for (const draft of remote) byUri.set(draft.uri, draft)
  return order([...byUri.values()])
}

/**
 * Applies a merge and persists it, so the next cold start already has it.
 * Returns the new index for the caller's state.
 */
export function syncCapsuleDrafts(
  remote: CapsuleDraftRef[],
): CapsuleDraftRef[] {
  const next = mergeCapsuleDrafts(getCapsuleDrafts(), remote)
  device.set(['capsuleDrafts'], next)
  return next
}
